import * as THREE from 'three'
import { Vec3 } from '../core/math/vec3'
import { CelestialBody } from '../core/world/body'
import { toScene } from './renderer'

const ZERO_MONDO: Vec3 = { x: 0, y: 0, z: 0 }

/**
 * Rilievo procedurale, deterministico e senza dipendenze: tre onde incrociate
 * bastano a rompere l'uniformita di una sfera e a dare punti di riferimento
 * per capire che il pianeta ruota. Il terreno vero, con quote e collisioni,
 * e materia della fase 5.
 */
function rilievo(x: number, y: number, z: number): number {
  return (
    Math.sin(x * 3.1) * Math.cos(y * 2.7) +
    Math.sin(y * 4.3 + 1.2) * Math.cos(z * 3.9) * 0.8 +
    Math.sin(z * 5.1 + 2.4) * Math.cos(x * 4.7) * 0.6
  )
}

function planetMesh(body: CelestialBody): THREE.Mesh {
  const geom = new THREE.IcosahedronGeometry(body.radius, 6)
  const pos = geom.getAttribute('position')
  const colori = new Float32Array(pos.count * 3)

  const mare = new THREE.Color(0x1d4a72)
  const costa = new THREE.Color(0x3f7f5e)
  const terra = new THREE.Color(0x6d7a45)
  const roccia = new THREE.Color(0x8a8377)
  const c = new THREE.Color()

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i) / body.radius
    const y = pos.getY(i) / body.radius
    const z = pos.getZ(i) / body.radius
    const h = rilievo(x, y, z)

    if (h < -0.15) c.copy(mare)
    else if (h < 0.1) c.copy(costa)
    else if (h < 0.9) c.copy(terra)
    else c.copy(roccia)

    // calotte polari: la latitudine e |z| perche il polo nord e +Z
    const lat = Math.abs(z)
    if (lat > 0.88) c.lerp(new THREE.Color(0xe8eef2), (lat - 0.88) / 0.12)

    colori[i * 3] = c.r
    colori[i * 3 + 1] = c.g
    colori[i * 3 + 2] = c.b
  }

  geom.setAttribute('color', new THREE.BufferAttribute(colori, 3))
  return new THREE.Mesh(geom, new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.94,
    metalness: 0,
    flatShading: true,
  }))
}

/**
 * Guscio atmosferico.
 *
 * Non basta un fresnel di bordo: quello disegna l'alone visto da fuori, ma
 * lascia il cielo NERO a chi sta a terra, che e la meta del gioco. Qui si
 * campiona la densita lungo il raggio che va dalla camera al guscio, e si
 * accumula la profondita ottica. Ne viene fuori da solo il comportamento
 * giusto in tutti e tre i casi: cielo azzurro dal suolo, orizzonte piu carico
 * dello zenit, e alone sottile visto dallo spazio.
 */
function atmosphereMesh(body: CelestialBody): { mesh: THREE.Mesh; material: THREE.ShaderMaterial } | null {
  if (!body.atmosphere) return null
  const raggio = body.radius + body.atmosphere.ceiling

  const material = new THREE.ShaderMaterial({
    transparent: true,
    side: THREE.BackSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      tinta: { value: new THREE.Color(0x5c9fdd) },
      cameraLocale: { value: new THREE.Vector3() },
      raggioSuolo: { value: body.radius },
      altezzaScala: { value: body.atmosphere.scaleHeight },
      intensita: { value: 1.35 },
    },
    vertexShader: `
      varying vec3 vLocale;
      void main() {
        vLocale = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `
      uniform vec3 tinta;
      uniform vec3 cameraLocale;
      uniform float raggioSuolo;
      uniform float altezzaScala;
      uniform float intensita;
      varying vec3 vLocale;

      void main() {
        vec3 P = cameraLocale;
        vec3 Q = vLocale;
        float lunghezza = length(Q - P);

        // profondita ottica: densita esponenziale campionata lungo il raggio
        const int CAMPIONI = 8;
        float somma = 0.0;
        for (int i = 0; i < CAMPIONI; i++) {
          float t = (float(i) + 0.5) / float(CAMPIONI);
          vec3 S = mix(P, Q, t);
          float h = max(length(S) - raggioSuolo, 0.0);
          somma += exp(-h / altezzaScala);
        }
        float profondita = somma / float(CAMPIONI) * lunghezza / altezzaScala;

        float a = 1.0 - exp(-profondita * intensita);
        gl_FragColor = vec4(tinta * a, a);
      }`,
  })

  return { mesh: new THREE.Mesh(new THREE.IcosahedronGeometry(raggio, 5), material), material }
}

export interface PlanetView {
  readonly group: THREE.Group
  update(time: number, origin: Vec3): void
}

export function createPlanet(body: CelestialBody): PlanetView {
  const group = new THREE.Group()
  const superficie = planetMesh(body)
  group.add(superficie)

  const aria = atmosphereMesh(body)
  if (aria) group.add(aria.mesh)

  return {
    group,
    update(time: number, origin: Vec3): void {
      // la camera, in coordinate locali del pianeta, e l'origine flottante:
      // il guscio ne ha bisogno per sapere quanta aria attraversa lo sguardo
      if (aria) aria.material.uniforms.cameraLocale!.value.set(origin.x, origin.y, origin.z)
      // il pianeta e centrato nell'origine del mondo; con l'origine flottante
      // finisce a −origin, cioe sotto i piedi del veicolo
      group.position.copy(toScene(ZERO_MONDO, origin))
      // ruota su +Z, come vuole atmosphereVelocity in core/dynamics
      superficie.rotation.z = body.rotationPeriod > 0
        ? (2 * Math.PI * time) / body.rotationPeriod
        : 0
    },
  }
}
